import { Injectable, Signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import {
  AutoOrient,
  AutoOrientService,
  AutoOrientStatusStore,
  AutoOrientStatusWsEvent,
} from '@infinitform/platform-core';
import { CLOUD_PLATFORM_CONFIG } from '../config';

@Injectable()
export class CloudAutoOrientService implements AutoOrientService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CLOUD_PLATFORM_CONFIG);
  private readonly store = new AutoOrientStatusStore();

  statusFor(projectId: string): Signal<AutoOrient | null> {
    return this.store.statusFor(projectId);
  }

  seed(projectId: string, job: AutoOrient | null): void {
    this.store.seed(projectId, job);
  }

  handleWsEvent(event: AutoOrientStatusWsEvent): void {
    if (event.type !== 'auto-orient:status' || !event.autoOrient) return;
    this.store.apply(event.projectId, event.autoOrient);
  }

  async start(projectId: string, critAngleDeg: number): Promise<AutoOrient> {
    const previous = this.store.markRequested(projectId, critAngleDeg);
    try {
      const { autoOrient } = await lastValueFrom(
        this.http.post<{ autoOrient: AutoOrient }>(
          `${this.config.apiBaseUrl}/project/${projectId}/auto-orient`,
          { critAngleDeg },
        ),
      );
      this.store.apply(projectId, autoOrient);
      return autoOrient;
    } catch (err) {
      this.store.rollback(projectId, previous);
      throw new Error(messageOf(err));
    }
  }
}

function messageOf(err: unknown): string {
  const body = (err as { error?: { message?: unknown } } | null)?.error;
  if (body && typeof body.message === 'string' && body.message) return body.message;
  if (err instanceof Error && err.message) return err.message;
  return 'Auto-orient could not be started';
}
